import React, { FormEvent, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Checkbox, Dropdown, Icon, Input, Textarea } from '@column/column-ui-kit';

import { RecaptchaAction, useReCAPTCHA } from '~/lib/recaptcha';
import { Option, OptionLabel } from '~/public/pages/_components';
import { WebsiteRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import {
  Hint,
  PlatformInfoFormElement,
  PlatformInfoFormLabelInline,
  PlatformInfoFormLabelTooltip,
  SmallHeadline,
} from '~/styles';
import { validateEmail } from '~/util';

const COMPANY_STAGES = ['Pre-raise / bootstrapped', 'Under $5M raised', 'Over $5M raised', 'Public'];
const CUSTOMER_TYPES = ['Businesses', 'Consumers'];

const Grid = styled.div`
  display: grid;
  grid-gap: 48px;

  @media (max-width: 600px) {
    grid-gap: 32px;
  }
`;

const FormSection = styled.div`
  display: grid;
  grid-gap: 24px;

  @media (min-width: 601px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const FormWrapper = styled.div`
  display: grid;
  grid-gap: 16px;
`;

const FormHeader = styled.div`
  display: grid;
  grid-gap: 4px;
`;

const StyledTextarea = styled(Textarea)`
  min-height: 120px;
`;

const OptionList = styled.div`
  display: grid;
  grid-gap: 8px;

  @media (min-width: 601px) {
    grid-gap: 12px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ComplianceList = styled.div`
  display: grid;
  grid-gap: 8px;

  @media (min-width: 601px) {
    max-width: 60%;
    grid-gap: 12px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const ComplianceListOption = styled(Option)<{ isActive?: boolean }>`
  display: flex;
  justify-content: center;
  text-align: center;
  color: ${({ theme }) => theme.secondary.background};

  ${({ isActive }) =>
    isActive &&
    css`
      color: ${({ theme }) => theme.primary.background};
      box-shadow: inset 0 0 0 1px ${({ theme }) => theme.primary.background};
    `}
`;

export interface ContactFormProps {
  initialValues?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

export interface ContactFormRef {
  submit: () => void;
}

export const ContactForm = forwardRef<ContactFormRef, ContactFormProps>(({ initialValues }, ref) => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const addSuccessNotification = useNotificationStore((s) => s.addSuccessNotification);
  const formElementRef = useRef<HTMLFormElement>(null);
  const [firstName, setFirstName] = useState<string>(initialValues?.firstName ?? '');
  const [lastName, setLastName] = useState<string>(initialValues?.lastName ?? '');
  const [email, setEmail] = useState<string>(initialValues?.email ?? '');
  const [companyName, setCompanyName] = useState<string>('');
  const [companySize, setCompanySize] = useState<string>('');
  const [companyWebsite, setCompanyWebsite] = useState<string>('');
  const [companyStage, setCompanyStage] = useState<string>('');
  const [howDidYouHear, setHowDidYouHear] = useState<string>('');
  const [whatAreYouBuilding, setWhatAreYouBuilding] = useState<string>('');
  const [complianceCapabilities, setComplianceCapabilities] = useState<string>('');
  const [otherComment, setOtherComment] = useState<string>('');
  const [customerTypes, setCustomerTypes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const executeReCAPTCHA = useReCAPTCHA();

  useImperativeHandle(ref, () => ({
    submit: () => {
      formElementRef.current?.requestSubmit();
    },
  }));

  const resetFields = () => {
    setFirstName(initialValues?.firstName ?? '');
    setLastName(initialValues?.lastName ?? '');
    setEmail(initialValues?.email ?? '');
    setCompanyName('');
    setCompanySize('');
    setCompanyWebsite('');
    setCompanyStage('');
    setHowDidYouHear('');
    setWhatAreYouBuilding('');
    setComplianceCapabilities('');
    setOtherComment('');
    setCustomerTypes([]);
  };

  const toggleCustomerType = (option: string) =>
    setCustomerTypes((state) => (state.includes(option) ? state.filter((i) => i !== option) : [...state, option]));

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (isLoading) {
      return;
    }

    const missing = [
      [!firstName || !lastName, 'Please enter your first and last name'],
      [!email || !validateEmail(email), 'Please enter a valid email address'],
      [!companyWebsite, 'Please enter your company website'],
      [!companyStage, 'Please select your company stage'],
      [customerTypes.length < 1, 'Please select who your customers are'],
      [!whatAreYouBuilding, 'Please describe what you are building'],
      [!complianceCapabilities, 'Please select whether you intend to operate your own compliance program'],
    ].find(([isMissing]) => isMissing);

    if (missing) {
      addDangerNotification({ content: missing[1] as string, display: 'global' });
      return;
    }

    setIsLoading(true);

    const data = {
      origin: 'Dashboard' as const,
      firstName,
      lastName,
      emailAddress: email,
      companyName,
      companySize,
      companyWebsite,
      companyStage,
      howDidYouHear,
      whatAreYouBuilding,
      complianceCapabilities,
      otherComment,
      customerTypes: customerTypes.join(', '),
    };

    executeReCAPTCHA(RecaptchaAction.Contact)
      .then((reCaptchaToken: string) =>
        WebsiteRepository.contact(data, reCaptchaToken)
          .then(() => {
            resetFields();
            addSuccessNotification({
              content: "Thanks for reaching out. We'll be in touch shortly.",
              display: 'global',
            });
          })
          .catch((error) => {
            addDangerNotification({ content: error.message, display: 'global' });
          })
          .finally(() => setIsLoading(false))
      )
      .catch((err: Error) => {
        setIsLoading(false);
        addDangerNotification({ content: err.message, display: 'global' });
      });
  };

  return (
    <Grid as="form" ref={formElementRef} onSubmit={handleSubmit}>
      <FormSection>
        <PlatformInfoFormElement>
          <PlatformInfoFormLabelInline>First name</PlatformInfoFormLabelInline>
          <Input placeholder="First name" onChange={(value: string) => setFirstName(value)} value={firstName} />
        </PlatformInfoFormElement>

        <PlatformInfoFormElement>
          <PlatformInfoFormLabelInline>Last name</PlatformInfoFormLabelInline>
          <Input placeholder="Last name" onChange={(value: string) => setLastName(value)} value={lastName} />
        </PlatformInfoFormElement>

        <PlatformInfoFormElement>
          <PlatformInfoFormLabelInline>Your email</PlatformInfoFormLabelInline>
          <Input placeholder="Email address" onChange={(value: string) => setEmail(value)} value={email} />
        </PlatformInfoFormElement>

        <PlatformInfoFormElement>
          <PlatformInfoFormLabelInline>Company Website</PlatformInfoFormLabelInline>
          <Input
            placeholder="https://example.com"
            onChange={(value: string) => setCompanyWebsite(value)}
            value={companyWebsite}
          />
        </PlatformInfoFormElement>

        <PlatformInfoFormElement>
          <PlatformInfoFormLabelInline>
            Company name
            <PlatformInfoFormLabelTooltip content="The name of the company you work or are representing for this financial product">
              <Icon.CircleQuestionmark />
            </PlatformInfoFormLabelTooltip>
          </PlatformInfoFormLabelInline>
          <Input placeholder="Company name" onChange={(value: string) => setCompanyName(value)} value={companyName} />
        </PlatformInfoFormElement>

        <PlatformInfoFormElement>
          <PlatformInfoFormLabelInline>
            Number of employees
            <PlatformInfoFormLabelTooltip content="The number of people who work at the company">
              <Icon.CircleQuestionmark />
            </PlatformInfoFormLabelTooltip>
          </PlatformInfoFormLabelInline>
          <Input placeholder="50" onChange={(value: string) => setCompanySize(value)} value={companySize} />
        </PlatformInfoFormElement>
      </FormSection>

      <FormWrapper>
        <FormHeader>
          <SmallHeadline>What stage is your company?</SmallHeadline>
        </FormHeader>

        <Dropdown
          fullWidth
          placeholder="Select stage"
          active={companyStage || undefined}
          onChange={(value) => setCompanyStage(String(value))}
          options={COMPANY_STAGES.map((stage) => ({ value: stage, label: stage }))}
        />
      </FormWrapper>

      <FormWrapper>
        <FormHeader>
          <SmallHeadline>Who are your customers?</SmallHeadline>
          <Hint>(Select all that apply)</Hint>
        </FormHeader>

        <OptionList>
          {CUSTOMER_TYPES.map((option: string) => (
            <Option
              key={option}
              isSimple
              isActive={customerTypes.includes(option)}
              onClick={() => toggleCustomerType(option)}
            >
              <Checkbox
                isChecked={customerTypes.includes(option)}
                onCheckedChange={() => toggleCustomerType(option)}
                label={option}
              />
            </Option>
          ))}
        </OptionList>
      </FormWrapper>

      <FormWrapper>
        <FormHeader>
          <SmallHeadline>How did you hear about us?</SmallHeadline>
        </FormHeader>
        <Input
          placeholder="e.g. a referral, a conference, search, a podcast"
          onChange={(value: string) => setHowDidYouHear(value)}
          value={howDidYouHear}
        />
      </FormWrapper>

      <FormWrapper>
        <FormHeader>
          <SmallHeadline>What are you building?</SmallHeadline>
        </FormHeader>
        <StyledTextarea
          placeholder="Please describe your product or service and how you plan to use Column's banking infrastructure"
          onChange={(value: string) => setWhatAreYouBuilding(value)}
          value={whatAreYouBuilding}
        />
      </FormWrapper>

      <FormWrapper>
        <FormHeader>
          <SmallHeadline>
            Do you intend to operate your own compliance&nbsp;program?
            <PlatformInfoFormLabelTooltip
              content="Compliance helps ensure adherence to applicable laws, regulations and internal policies and procedures"
              largeMargin
            >
              <Icon.CircleQuestionmark />
            </PlatformInfoFormLabelTooltip>
          </SmallHeadline>
        </FormHeader>

        <ComplianceList>
          <ComplianceListOption
            isSimple
            isActive={complianceCapabilities === 'Yes'}
            onClick={() => setComplianceCapabilities('Yes')}
          >
            <OptionLabel>Yes</OptionLabel>
          </ComplianceListOption>
          <ComplianceListOption
            isSimple
            isActive={complianceCapabilities === 'No'}
            onClick={() => setComplianceCapabilities('No')}
          >
            <OptionLabel>No</OptionLabel>
          </ComplianceListOption>
          <ComplianceListOption
            isSimple
            isActive={complianceCapabilities === 'Not sure'}
            onClick={() => setComplianceCapabilities('Not sure')}
          >
            <OptionLabel>Not sure</OptionLabel>
          </ComplianceListOption>
        </ComplianceList>
      </FormWrapper>

      <FormWrapper>
        <FormHeader>
          <SmallHeadline>Please include any other questions or comments you&nbsp;may&nbsp;have.</SmallHeadline>
        </FormHeader>
        <StyledTextarea
          placeholder="Comments"
          onChange={(value: string) => setOtherComment(value)}
          value={otherComment}
        />
      </FormWrapper>
    </Grid>
  );
});

ContactForm.displayName = 'ContactForm';
